import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useMemo } from 'react';

import type { ElementState } from '@src/renderer/ElementState';

export const useTimelineTracks = () => {
  const videoCreator = useVideoCreatorStore();

  // Calculate tracks with proper ordering and filtering
  const tracks = useMemo(() => {
    let tracksArray = Array.from(videoCreator.tracks?.entries() ?? []);
    tracksArray.reverse();

    tracksArray = tracksArray.filter(([track]) => track < KARAOKE_TRACK_NUMBER);
    if (videoCreator.tracks?.get(KARAOKE_TRACK_NUMBER)) {
      const videoTrack = tracksArray.findIndex(([, elements]) =>
        videoCreator.isOriginalVideoElement(elements[0].source),
      );

      tracksArray.splice(videoTrack + 1, 0, [
        KARAOKE_TRACK_NUMBER,
        videoCreator.tracks?.get(KARAOKE_TRACK_NUMBER)!,
      ]);
    }
    return tracksArray;
  }, [videoCreator.tracks]);

  // Calculate active tracks
  const activeTracks = useMemo(() => {
    return tracks
      .filter(
        ([track, elements]) =>
          elements.some(e =>
            videoCreator.activeElementIds.includes(e.source.id),
          ) ||
          (track === videoCreator.selectedTrack &&
            track !== KARAOKE_TRACK_NUMBER),
      )
      .map(([t]) => t);
  }, [tracks, videoCreator.activeElementIds, videoCreator.selectedTrack]);

  // Calculate total track height
  const trackHeight = useMemo(() => {
    return tracks.reduce((acc, curr) => {
      const [, elements] = curr;
      const isOriginalVideo = videoCreator.isOriginalVideoElement(
        elements[0].source,
      );
      const isText =
        elements[0].source.type === 'text' ||
        elements[0].source.track === KARAOKE_TRACK_NUMBER;
      if (isOriginalVideo) return acc + 99;
      if (isText) return acc + 38;
      return acc + 78;
    }, 20);
  }, [tracks, videoCreator]);

  // Get track information helper
  const getTrackInfo = (track: number, elements: ElementState[]) => {
    const isOriginalVideo = videoCreator.isOriginalVideoElement(
      elements[0].source,
    );
    const isText =
      elements[0].source.type === 'text' ||
      elements[0].source.track === KARAOKE_TRACK_NUMBER;
    const isShape = elements[0].source.type === 'shape';
    const isComposition =
      elements[0].source.type === 'composition' &&
      !videoCreator.isImageElementComposition(elements[0]);
    const isLogo = elements.some(e => videoCreator.isLogoElement(e));
    const isActive = activeTracks.includes(track);

    return {
      isOriginalVideo,
      isText,
      isShape,
      isComposition,
      isLogo,
      isActive,
      height: isOriginalVideo
        ? 99
        : isText || isLogo || isShape || isComposition
          ? 38
          : 78,
    };
  };

  return {
    tracks,
    activeTracks,
    trackHeight,
    getTrackInfo,
  };
};
